import { useEffect, useState, useRef } from 'react';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Box, Switch, Select, MenuItem, Paper, Stack, alpha, useTheme, Typography } from '@mui/material';
import FlipCameraAndroidOutlinedIcon from '@mui/icons-material/FlipCameraAndroidOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import { ItemPaper } from '../../../../components';
import { BarcodeInput } from '../../../../components';

import { ItemAcceptanceCancellationCommand } from '../../../../../services/studio/StudioCommand';
import { ItemAcceptanceCancellationQuery } from '../../../../../services/studio/StudioQuery';
import { playBeep } from '../../../../helper/studio_helper';

import '../../../../style/studio.scss';

const CONTROL_HEIGHT = 48;
/** Unified outlined-field styling (same hover/focus as BarcodeInput). */
const FIELD_BORDER = '#E5E7EB';
const FIELD_BORDER_HOVER = '#CBD5E1';
const FIELD_BORDER_FOCUS = '#94A3B8';
const FIELD_FOCUS_RING = 'rgba(148, 163, 184, 0.22)';

const DEFAULT_RESHOT_REASON_ID = 3;

const isMatchingSetTrue = (value: unknown): boolean =>
    value === true || value === 'true' || value === 1 || value === '1';

export const ItemAcceptance = ({ setIsLoading }: any) => {
    const [isReshot, setIsReshot] = useState<boolean>(false);
    const [reshotReasonId, setReshotReasonId] = useState<any>(null);
    const [itemData, setItemData] = useState<any[]>([]);
    const [reshotReasons, setReshotReasons] = useState<any[]>([]);
    const [barcode, setBarcode] = useState<string>('');

    const barcodeInputRef = useRef<HTMLInputElement>(null);
    const detailsSectionRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    const { t } = useTranslation();

    const getReshotReasonLabel = (reason: any): string => (reason?.reasonDescription || reason?.reason || '').trim();

    const loadMatchingSetForItems = (items: any[]) => {
        const itemsWithLoading = items.map((item: any) => ({
            ...item,
            matchingSetLoading: Boolean(item.specialCode && item.colorCode),
            isMatchingSet: undefined,
        }));
        setItemData(itemsWithLoading);

        itemsWithLoading.forEach((item: any) => {
            if (!item.specialCode || !item.colorCode) {
                return;
            }

            ItemAcceptanceCancellationQuery.GetMatchingSetByModelOption({
                specialCode: item.specialCode,
                colorCode: item.colorCode,
            })
                .then((matchRes: any) => {
                    const matchingSet =
                        matchRes?.data?.matchingSet ?? matchRes?.data?.MatchingSet ?? false;
                    setItemData((prev) =>
                        prev.map((p: any) =>
                            p.barcode === item.barcode
                                ? { ...p, isMatchingSet: matchingSet, matchingSetLoading: false }
                                : p
                        )
                    );
                    if (isMatchingSetTrue(matchingSet)) {
                        Swal.fire({
                            icon: 'warning',
                            title: t('studio.itemAcceptanceCancellation.matchingSetWarningMessage'),
                            showConfirmButton: false,
                            timer: 1500,
                        });
                    }
                })
                .catch(() => {
                    setItemData((prev) =>
                        prev.map((p: any) =>
                            p.barcode === item.barcode
                                ? { ...p, isMatchingSet: false, matchingSetLoading: false }
                                : p
                        )
                    );
                });
        });
    };

    const labelSx = {
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 0.25,
        mb: 0.75,
        fontSize: '0.7rem',
        fontWeight: 700,
        color: '#6B7280',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.06em',
    };

    const outlinedControlSx = {
        height: CONTROL_HEIGHT,
        minHeight: CONTROL_HEIGHT,
        borderRadius: '10px',
        backgroundColor: '#fff',
        border: `1.5px solid ${FIELD_BORDER}`,
        px: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 1,
        boxSizing: 'border-box',
        transition: 'border-color 0.18s ease, background-color 0.18s ease',
        '&:hover': {
            borderColor: FIELD_BORDER_HOVER,
            backgroundColor: '#FAFAFA',
        },
    };

    /**
     * MUI 7 `Select` merges `sx` onto `OutlinedInput` itself — do not use `& .MuiOutlinedInput-root`
     * (that only matches a nested node; there is none). Style the root with `& fieldset`, etc.
     */
    const selectSx = {
        height: CONTROL_HEIGHT,
        minHeight: CONTROL_HEIGHT,
        maxHeight: CONTROL_HEIGHT,
        borderRadius: '10px',
        backgroundColor: '#fff',
        alignItems: 'center',
        boxSizing: 'border-box',
        padding: 0,
        transition: 'all 0.18s ease',
        '& fieldset': {
            borderColor: FIELD_BORDER,
            borderWidth: '1.5px',
            borderStyle: 'solid',
        },
        '&:hover fieldset': {
            borderColor: FIELD_BORDER_HOVER,
        },
        '&.Mui-focused fieldset': {
            borderColor: FIELD_BORDER_FOCUS,
            boxShadow: `0 0 0 4px ${FIELD_FOCUS_RING}`,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderStyle: 'solid',
            borderColor: FIELD_BORDER,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: FIELD_BORDER_HOVER,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: FIELD_BORDER_FOCUS,
        },
        '&.Mui-error fieldset': {
            borderColor: FIELD_BORDER,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: `${FIELD_BORDER} !important`,
        },
        '&.Mui-error:hover fieldset': {
            borderColor: FIELD_BORDER_HOVER,
        },
        '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: `${FIELD_BORDER_HOVER} !important`,
        },
        '&.Mui-error.Mui-focused fieldset': {
            borderColor: FIELD_BORDER_FOCUS,
            boxShadow: `0 0 0 4px ${FIELD_FOCUS_RING}`,
        },
        '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${FIELD_BORDER_FOCUS} !important`,
        },
        '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            boxSizing: 'border-box',
            height: CONTROL_HEIGHT,
            minHeight: CONTROL_HEIGHT,
            maxHeight: CONTROL_HEIGHT,
            py: '0 !important',
            pt: '0 !important',
            pb: '0 !important',
            pl: '14px',
            pr: '40px !important',
            fontSize: '0.95rem',
            fontWeight: 500,
            lineHeight: `${CONTROL_HEIGHT}px`,
        },
        '& .MuiOutlinedInput-input': {
            padding: '0 40px 0 14px !important',
            height: CONTROL_HEIGHT,
            minHeight: CONTROL_HEIGHT,
            maxHeight: CONTROL_HEIGHT,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.95rem',
            fontWeight: 500,
        },
        '& .MuiSelect-icon': {
            top: '50%',
            transform: 'translateY(-50%)',
            right: 10,
        },
    };

    const handleEnterBarcode = (barcode: any) => {
        if (isReshot && !reshotReasonId) {
            Swal.fire({
                icon: 'error',
                title: t('studio.itemAcceptanceCancellation.requiredReshotReason'),
                showConfirmButton: false,
                timer: 1500,
            });
            setBarcode('');
            setTimeout(() => barcodeInputRef.current?.focus(), 0);
            return;
        }
        setIsLoading(true);
        ItemAcceptanceCancellationCommand.ProductAcceptanceAdd({
            barcode,
            isReshot,
            reshotReasonId,
        })
            .then((res: any) => {
                if (res.errors) {
                    const errorMessages = Object.values(res.errors).flat().join('\n');
                    Swal.fire(errorMessages, '', 'error');
                    setBarcode('');
                } else if (res.success === false) {
                    Swal.fire(res.error || t('general.anErrorOccured'), '', 'error');
                    setBarcode('');
                } else {
                    const acceptanceItems = res.model?.productAcceptanceItems || [];
                    loadMatchingSetForItems(acceptanceItems);
                    if (
                        acceptanceItems.some(
                            (item: any) => item.responseStatus === 2 || item.responseStatus === 3
                        )
                    ) {
                        playBeep();
                    }
                    setBarcode('');
                }

                setReshotReasonId(isReshot ? DEFAULT_RESHOT_REASON_ID : null);
                setIsLoading(false);
                setTimeout(() => barcodeInputRef.current?.focus(), 0);
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
                setBarcode('');
                setIsLoading(false);
                setTimeout(() => barcodeInputRef.current?.focus(), 0);
            });
    };

    useEffect(() => {
        ItemAcceptanceCancellationQuery.GetItemAcceptancePageData()
            .then((res: any) => {
                if (res.errors) {
                    const errorMessages = Object.values(res.errors).flat().join('\n');
                    Swal.fire(errorMessages, '', 'error');
                } else {
                    if (res.data) {
                        setReshotReasons(res.data?.reshotReasons);
                    }
                }

                setIsLoading(false);
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured', '', 'error'));
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!itemData?.length) return;
        const id = window.setTimeout(() => {
            detailsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 80);
        return () => window.clearTimeout(id);
    }, [itemData]);

    return (
        <Box sx={{ p: 2 }}>
            <Stack spacing={2} sx={{ width: '100%', mt: 1 }}>
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        borderRadius: 3,
                        border: '1px solid #EDEFF2',
                        backgroundColor: alpha(theme.palette.grey[50], 0.95),
                        boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.06)}, 0 2px 6px ${alpha(
                            theme.palette.common.black,
                            0.04
                        )}`,
                        transition: 'box-shadow 0.2s ease',
                        '&:hover': {
                            boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.08)}`,
                        },
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', lg: 'flex-end' }}
                        flexWrap="wrap"
                        sx={{ p: 2 }}
                    >
                        <BarcodeInput
                            autoFocus
                            onValidBarcodeEnter={handleEnterBarcode}
                            onChange={setBarcode}
                            value={barcode}
                            inputRef={barcodeInputRef}
                            controlHeight={CONTROL_HEIGHT}
                        />

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexShrink: 0,
                                minWidth: { xs: '100%', sm: 220 },
                            }}
                        >
                            <Typography sx={{ ...labelSx, gap: 0 }}>{t('studio.repeatedShot')}</Typography>
                            <Box sx={{ ...outlinedControlSx, gap: 1.25 }}>
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '9px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        backgroundColor: '#F3F4F6',
                                        color: '#475569',
                                        border: `1px solid ${FIELD_BORDER}`,
                                    }}
                                >
                                    <FlipCameraAndroidOutlinedIcon sx={{ fontSize: '1.2rem' }} />
                                </Box>
                                <Switch
                                    checked={isReshot}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        const checked = event.target.checked;
                                        setIsReshot(checked);
                                        setReshotReasonId(checked ? DEFAULT_RESHOT_REASON_ID : null);
                                    }}
                                    sx={{
                                        m: 0,
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: theme.palette.primary.main,
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.45),
                                        },
                                    }}
                                />
                            </Box>
                        </Box>

                        {isReshot && (
                            <Box sx={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column' }}>
                                <Typography sx={labelSx}>
                                    <FormatListBulletedOutlinedIcon sx={{ fontSize: '0.95rem', color: '#6B7280' }} />
                                    {t('studio.repeatedShotReason')}
                                </Typography>
                                <Box sx={{ ...outlinedControlSx, gap: 0, padding: 0, height: '100%' }}>
                                    <Select
                                        value={reshotReasonId}
                                        onChange={(event) => {
                                            setReshotReasonId(event.target.value);
                                            setTimeout(() => barcodeInputRef.current?.focus(), 0);
                                        }}
                                        displayEmpty
                                        fullWidth
                                        error={false}
                                        sx={selectSx}
                                        renderValue={(selected: any) => {
                                            if (!selected) {
                                                return (
                                                    <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
                                                        {t('studio.repeatedShotReason')}
                                                    </Typography>
                                                );
                                            }
                                            const found = (reshotReasons || []).find(
                                                (r: any) => String(r.id) === String(selected)
                                            );
                                            return found ? getReshotReasonLabel(found) : '';
                                        }}
                                    >
                                        {(reshotReasons || []).map((reason: any) => (
                                            <MenuItem key={reason.id} id={reason.id} value={reason.id}>
                                                {getReshotReasonLabel(reason)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </Paper>

                {itemData?.length > 0 && (
                    <Box ref={detailsSectionRef} sx={{ width: '100%', pt: 0.5, scrollMarginTop: { xs: 12, sm: 16 } }}>
                        {itemData.map((item: any) => (
                            <Box sx={{ width: '100%', pt: 0.5 }} key={item.barcode}>
                                <ItemPaper item={item} />
                            </Box>
                        ))}
                    </Box>
                )}
            </Stack>
        </Box>
    );
};
